import { trackedMap, trackedSet } from '@ember/reactive/collections';
import Service, { type Registry as Services, service } from '@ember/service';
import { isTesting, macroCondition } from '@embroider/macros';
import { tracked } from '@glimmer/tracking';
import type { Dayjs } from 'dayjs';
import { task, timeout } from 'ember-concurrency';
import {
  formatUrl,
  type FormatUrlParams,
} from 'frontend-utils/utils/url/format-url';
import type { Carecommendation } from 'lumen/models';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { CarecommendationActionEntry } from 'lumen/services/pricing-assistant-handle-recommendation-actions-service';
import { RECOMMENDATION_ACTION_STATES } from 'lumen/utils/constants/pricing-assistant-constants';
import { logMessage } from 'lumen/utils/log-sentry-breadcrumb';

type BulkPushStatus = {
  failed: number;
  pushed: number;
  pushing: number;
  total: number;
};

// In this service we poll an endpoint that returns an array of recommendations that we're following up on (to know if they are pushed or not)
// The recommendations in this response never get pushed into the store, so that's why we have this extra interface

export interface RequestRecommendationActionStatusesResponse {
  actions?: CarecommendationActionEntry[];
  bulk_push_completion?: Record<string, BulkPushStatus>;
  hasError?: boolean;
}

export default class PricingAssistantRecommendationActionPollerService extends Service {
  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];
  @service declare store: Services['store'];

  // Local object containing the latest status of bulk pushes
  @tracked bulkPushesStatuses: Record<string, BulkPushStatus> = {};

  // Local object containing all bulk pushes that we want to follow up their latest recommendation action states
  bulkPushesToFollow: Record<string, Carecommendation[]> = {};

  // We needed slightly different post-polling behavior and this was the least intrusive way for now
  channelPricingCallbackOnPushFinish:
    | null
    | ((
        hotelId: string,
        date: string,
      ) => Promise<{ carecommendation: Carecommendation[] }>) = null;

  // Tracked set containing date strings of recommendations that are currently being refreshed
  datesBeingRefreshed = trackedSet<string>();

  POLLING_INTERVAL = 2000;
  // Recommendations that can be affected by an override push, keyed by date (YYYY-MM-DD).
  // The keys drive the day's loading state, so a date belongs here for the whole push lifecycle even with no recommendations
  recommendationAffectedByOverridePush = trackedMap<
    string,
    Carecommendation[]
  >();

  // Local object containing all recommendations of which we want to follow up their latest recommendation action states (meaning that they are in an accepted/pushing state, and should end up in an end state soon)
  recommendationsToFollow: Record<string, Carecommendation> = {};

  // Tracked set containing ids of recommendations for which we are currently giving the successful push lottie animation time to finish
  recommendationsWithPushAnimation = trackedSet<string>();

  // Extra property introduced during Smart Distribution beta (we want to skip this animation there)
  // Note: in an ideal scenario this animation shouldn't be baked into this service, but handled in components
  skipAnimationTimer = false;

  // The duration of animation is 5000ms: animation.getDuration()
  SUCCESSFUL_PUSH_LOTTIE_ANIMATION_DURATION = 5000;

  // Polling task for bulk pushes (multiple recommendations spanning single or multiple days)
  pricingAssistantRecommendationActionIdPollerBulkPushTask = task(
    { restartable: true },
    async () => {
      // Perform request to retrieve up-to-date recommendation action statuses for all bulk push recommendations we're following
      const response =
        await this.requestRecommendationActionStatusesTask.perform({
          isBulkPush: true,
        });

      if (response.bulk_push_completion) {
        this.bulkPushesStatuses = response.bulk_push_completion;
      }

      if (!response.actions) {
        return;
      }

      // 1. Loop through each bulk push and update recommendations with the latest action states until all recommendations in the bulk push are in end state (pushed or exception)
      // 2. Update / re-request data once the recommendations in the bulk push reach the end action state
      // We await all bulk pushes here so completed ones are removed from
      // bulkPushesToFollow before the hasToPerformAgain check below. Otherwise
      // the check reads stale state and schedules another iteration that fires
      // a status request with an empty bulk_push_ids list (INDU-1684).
      // allSettled (not all) keeps the pushes fault-isolated: a throw while
      // processing one push must not abort the others or stop the polling loop.
      await Promise.allSettled(
        Object.keys(this.bulkPushesToFollow).map(async (bulkPushId) => {
          const recommendationsForBulkPushId =
            this.bulkPushesToFollow[bulkPushId];

          if (!recommendationsForBulkPushId) {
            return;
          }

          // Update recommendations with the latest action states returned by BE
          recommendationsForBulkPushId.forEach((rec) => {
            const action = response.actions?.find((res) => {
              return (
                res.recommendation_id === rec.id &&
                res.bulk_push_id?.toString() === bulkPushId
              );
            });

            if (action) {
              rec.action = action.action;
            } else {
              // Remove recommendation from bulkPushesToFollow if not returned in the response
              // This can happen if the recommendation was overwritten by a new recommendation action
              this.bulkPushesToFollow[bulkPushId] =
                this.bulkPushesToFollow[bulkPushId]?.filter(
                  (recommendation) => recommendation.id !== rec.id,
                ) ?? [];

              if (this.bulkPushesToFollow[bulkPushId]?.length === 0) {
                this.removeBulkPushToFollow(bulkPushId);
              }
            }
          });

          // Check if all recommendations in the bulk push are in end state
          const allEndState = recommendationsForBulkPushId.every((rec) =>
            this.isRecommendationInEndState(rec),
          );

          if (!allEndState) {
            return;
          }

          try {
            logMessage(
              {
                type: 'debug',
                message: `ca-recommendation-action-poller: bulk push ${bulkPushId} recommendations ready to be refreshed`,
              },
              this.errorTracking,
              this.access.isStaffUser,
            );

            const { minDate, maxDate } =
              this.calculateFormattedDateRangeForBulkPush(
                recommendationsForBulkPushId,
              );

            if (!minDate || !maxDate) {
              return;
            }

            recommendationsForBulkPushId.forEach((recommendation) => {
              // @ts-expect-error: Incorrect type
              this.datesBeingRefreshed.add(recommendation.fromDate);
            });

            // Re-request carate & carecommendation when polling for the day ends
            const results =
              await this.pricingAssistantDataRequestService.reRequestDataAfterPushTask.perform(
                {
                  hotelId: this.hotelId!,
                  fromDate: minDate,
                  endDate: maxDate,
                  fetchOverrides:
                    this.access.hasPricingAssistantDynamicRatePricingFeature,
                },
              );

            if (results === null) {
              return;
            }

            const { carecommendation } = results;

            // Rollback any pending changes (dirty records) in the store
            carecommendation.forEach((record) => record.rollbackAttributes());

            this.unloadPushedRecommendations(
              recommendationsForBulkPushId,
              carecommendation,
            );
            this.unloadRecommendationsAffectedByOverridePush(
              carecommendation,
              minDate,
            );
          } finally {
            recommendationsForBulkPushId.forEach((recommendation) => {
              // @ts-expect-error: Incorrect type
              this.datesBeingRefreshed.delete(recommendation.fromDate);
            });
            this.removeBulkPushToFollow(bulkPushId);
          }
        }),
      );

      // Stop polling if no bulkPushesToFollow
      const hasToPerformAgain =
        Object.keys(this.bulkPushesToFollow).length !== 0;

      if (!hasToPerformAgain) {
        return;
      }

      logMessage(
        {
          type: 'debug',
          message:
            'ca-recommendation-action-poller: requires another status request after timeout',
        },
        this.errorTracking,
        this.access.isStaffUser,
      );

      await timeout(this.POLLING_INTERVAL);

      void this.pricingAssistantRecommendationActionIdPollerBulkPushTask.perform();
    },
  );

  /**
   * Task that is performed when the polling in this service is triggered.
   * This task can call itself again when not all recommendation action statuses are not yet in an end state
   */
  pricingAssistantRecommendationActionIdPollerTask = task(
    { restartable: true },
    async (): Promise<void> => {
      // Perform request to retrieve up-to-date recommendation action statuses for all recommendations we're following
      const response =
        await this.requestRecommendationActionStatusesTask.perform();

      if (!response.actions) {
        return;
      }

      // 1. Loop until at least one entry with non-end action state i.e pushed or exception
      // 2. Update / re-request data once the recommendation when it reach the end action state
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      response.actions.forEach(async (res) => {
        const recommendation =
          this.recommendationsToFollow[res.recommendation_id];

        if (!recommendation) {
          return;
        }

        // Update recommendation states depending on the returned response
        recommendation.action = res.action;

        if (!this.isRecommendationInEndState(recommendation)) {
          return;
        }

        // Remove recommendation that is in end state from our local array (we don't need to poll this anymore)
        this.removeRecommendationToFollow(recommendation);

        // Start pushed animation in case of pushed end state
        if (
          res.action === RECOMMENDATION_ACTION_STATES.PUSHED &&
          !this.skipAnimationTimer
        ) {
          await this.successfulPushLottieAnimationTimerTask.perform(
            recommendation.id!,
          );
        }

        logMessage(
          {
            type: 'debug',
            message: `ca-recommendation-action-poller: recommendation ${recommendation.id}, actionId ${recommendation.recommendationActionId} ready to be refreshed`,
          },
          this.errorTracking,
          this.access.isStaffUser,
        );

        const formattedRecommendationFromDate =
          recommendation.fromDate.format('YYYY-MM-DD');

        this.datesBeingRefreshed.add(formattedRecommendationFromDate);

        let results = null;

        try {
          // If the user is in Channel Pricing, the original refresh logic is disabled to use onChannelPricingPollingFinished callback instead.
          if (this.channelPricingCallbackOnPushFinish) {
            await this.channelPricingCallbackOnPushFinish(
              this.hotelId!,
              formattedRecommendationFromDate,
            );
          } else {
            // Re-request carate & carecommendation when polling for the day ends
            results =
              await this.pricingAssistantDataRequestService.reRequestDataAfterPushTask.perform(
                {
                  hotelId: this.hotelId!,
                  fromDate: formattedRecommendationFromDate,
                  endDate: formattedRecommendationFromDate,
                },
              );
          }
        } finally {
          this.datesBeingRefreshed.delete(formattedRecommendationFromDate);
        }

        if (results === null) {
          return;
        }

        const { carecommendation } = results;

        // Rollback any pending changes (dirty records) in the store
        carecommendation.forEach((record) => record.rollbackAttributes());

        this.unloadPushedRecommendations([recommendation], carecommendation);
      });

      // Stop polling if no recommendationsToFollow
      const hasToPerformAgain =
        Object.keys(this.recommendationsToFollow).length !== 0;

      if (!hasToPerformAgain) {
        return;
      }

      logMessage(
        {
          type: 'debug',
          message:
            'ca-recommendation-action-poller: requires another status request after timeout',
        },
        this.errorTracking,
        this.access.isStaffUser,
      );

      await timeout(this.POLLING_INTERVAL);
      void this.pricingAssistantRecommendationActionIdPollerTask.perform();
    },
  );

  /**
   * Task that handles the request for the latest recommendation action statuses
   * We use the service array of recommendations to generate the list of ids of recommendation action statuses we're interested in
   */
  requestRecommendationActionStatusesTask = task(
    { restartable: true },
    async (options = { isBulkPush: false }) => {
      const emptyResponse: RequestRecommendationActionStatusesResponse = {};

      try {
        const requestParams: FormatUrlParams = {
          // @ts-expect-error: Type 'string | null' is not assignable to type 'string | number | boolean | string[] | number[]'.
          subscription_id: this.hotelId,
        };

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (options.isBulkPush) {
          const bulkPushIds = Object.keys(this.bulkPushesToFollow);

          // Guard against firing a status request without any bulk push ids.
          // The BE requires ids or bulk_push_ids, and formatUrl drops empty
          // arrays, so an empty list would produce a request with only
          // subscription_id and a 422 in return (INDU-1684).
          if (bulkPushIds.length === 0) {
            return emptyResponse;
          }

          requestParams['bulk_push_ids[]'] = bulkPushIds;

          logMessage(
            {
              type: 'debug',
              message: `ca-recommendation-action-poller: requesting status for bulk pushes ${bulkPushIds
                .join(' / ')
                .toString()}`,
            },
            this.errorTracking,
            this.access.isStaffUser,
          );
        } else {
          const recommendationActionIds = Object.values(
            this.recommendationsToFollow,
          ).map((recommendation) => recommendation.recommendationActionId);

          // Guard against firing a status request without any ids (see above).
          if (recommendationActionIds.length === 0) {
            return emptyResponse;
          }

          requestParams['ids[]'] = recommendationActionIds;

          logMessage(
            {
              type: 'debug',
              message: `ca-recommendation-action-poller: requesting status for ${Object.keys(
                this.recommendationsToFollow,
              )
                .join(' / ')
                .toString()}`,
            },
            this.errorTracking,
            this.access.isStaffUser,
          );
        }

        const requestUrl = formatUrl(
          '/apigateway/v1/app/pricing-assistant/recommendations/action/status',
          requestParams,
          this.ajax.host,
        );

        const response: RequestRecommendationActionStatusesResponse =
          await this.ajax.request(requestUrl, {
            contentType: 'application/json',
          });

        return response;
      } catch (error) {
        this.errorTracking.addBreadcrumb({
          message: 'Pricing Assistant Recommendation statuses request failed',
        });
        this.errorTracking.captureException(error);

        const response: RequestRecommendationActionStatusesResponse = {
          hasError: true,
        };
        return response;
      }
    },
  );

  /**
   * Task that basically gives the successful push lottie animation time to finish before refreshing the data
   * @param {String} recommendationId
   */
  successfulPushLottieAnimationTimerTask = task(
    {},
    async (recommendationId: string): Promise<void> => {
      this.recommendationsWithPushAnimation.add(recommendationId);

      await timeout(this.SUCCESSFUL_PUSH_LOTTIE_ANIMATION_DURATION);

      this.recommendationsWithPushAnimation.delete(recommendationId);
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelId() {
    return this.hotelAndMarketService.hotel?.id;
  }

  get recommendationDatesAffectedByOverridePush(): Set<string> {
    return new Set(this.recommendationAffectedByOverridePush.keys());
  }

  // Extra property introduced during Smart Distribution beta, as a way to hook into the pricing polling.

  addBulkPushesToFollow(recommendations: Carecommendation[]): void {
    // Populate bulkPushesToFollow: Group recommendations by bulk_push_id
    recommendations.forEach((recommendation) => {
      if (recommendation.bulkPushId) {
        if (!this.bulkPushesToFollow[recommendation.bulkPushId]) {
          this.bulkPushesToFollow[recommendation.bulkPushId] = [];
        }

        if (this.bulkPushesToFollow[recommendation.bulkPushId]) {
          this.bulkPushesToFollow[recommendation.bulkPushId]?.push(
            recommendation,
          );
        }
      }
    });

    if (macroCondition(isTesting())) {
      return;
    }

    if (Object.keys(this.bulkPushesToFollow).length) {
      this.startPolling({ isBulkPush: true });
    }
  }

  addRecommendationsAffectedByOverridePush(
    recommendationsByDate: Record<string, Carecommendation[]>,
  ): void {
    Object.entries(recommendationsByDate).forEach(([date, recommendations]) => {
      this.recommendationAffectedByOverridePush.set(date, recommendations);
    });
  }
  /**
   * Add a recommendation to the service array to follow up/poll for latest recommendationAction statuses
   * @param {Carecommendation[]} recommendations
   */

  addRecommendationsToFollow(recommendations: Carecommendation[]): void {
    recommendations.forEach((recommendation) => {
      // Only move forward if there's a recommendation id present, as we need that to request the action statuses
      if (recommendation.recommendationActionId) {
        // @ts-expect-error: Type 'null' cannot be used as an index type.
        this.recommendationsToFollow[recommendation.id] = recommendation;
      }
    });

    if (macroCondition(isTesting())) {
      return;
    }

    if (Object.keys(this.recommendationsToFollow).length) {
      this.startPolling();
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  calculateFormattedDateRangeForBulkPush(
    recommendationsForBulkPushId: Carecommendation[],
  ) {
    if (recommendationsForBulkPushId.length === 0) {
      return { minDate: null, maxDate: null };
    }

    const { minDate, maxDate } = recommendationsForBulkPushId.reduce(
      (
        acc: { maxDate: Dayjs | null; minDate: Dayjs | null },
        recommendation,
      ) => {
        const date = recommendation.fromDate;

        if (!acc.minDate || date.isBefore(acc.minDate)) {
          acc.minDate = date;
        }
        if (!acc.maxDate || date.isAfter(acc.maxDate)) {
          acc.maxDate = date;
        }
        return acc;
      },
      { minDate: null, maxDate: null },
    );

    if (minDate && maxDate) {
      return {
        minDate: minDate.format('YYYY-MM-DD'),
        maxDate: maxDate.format('YYYY-MM-DD'),
      };
    }

    return { minDate: null, maxDate: null };
  }

  /**
   * Check if a single recommendation is in an end state (pushed or exception)
   * @param {Carecommendation} recommendation - Recommendation to check
   * @returns {boolean} - True if recommendation is in end state
   */
  private isRecommendationInEndState(
    recommendation: Carecommendation,
  ): boolean {
    return (
      recommendation.action === RECOMMENDATION_ACTION_STATES.PUSHED ||
      recommendation.action === RECOMMENDATION_ACTION_STATES.EXCEPTION
    );
  }

  removeBulkPushToFollow(bulkPushId: string): void {
    const fromDate =
      this.bulkPushesToFollow[bulkPushId]?.[0]?.fromDate?.format('YYYY-MM-DD');

    delete this.bulkPushesToFollow[bulkPushId];

    if (fromDate) {
      // Note: we only have single-day override pushes for now, so we can safely assume that all recommendations affected by the override push share the same fromDate
      this.removeDateAffectedByOverridePush(fromDate);
    }

    logMessage(
      {
        type: 'debug',
        message: `ca-recommendation-action-poller: removing bulk push ${bulkPushId} from the service array`,
      },
      this.errorTracking,
      this.access.isStaffUser,
    );
    logMessage(
      {
        type: 'debug',
        data: this.recommendationsToFollow,
        message: `ca-recommendation-action-poller: current bulkPushesToFollow length: ${
          Object.keys(this.bulkPushesToFollow).length
        }`,
      },
      this.errorTracking,
      this.access.isStaffUser,
    );
  }

  /**
   * Remove a date from the override push tracking (ends the loading state for that day)
   * @param {string} date - Date key (YYYY-MM-DD)
   */
  removeDateAffectedByOverridePush(date: string): void {
    this.recommendationAffectedByOverridePush.delete(date);
  }

  /**
   * Removes a recommendation from the service array to follow up/poll for latest recommendationAction statuses
   * @param {recommendation} recommendation : recommendation store record
   */

  removeRecommendationToFollow(recommendation: Carecommendation): void {
    // @ts-expect-error: Type 'null' cannot be used as an index type.
    delete this.recommendationsToFollow[recommendation.id];

    logMessage(
      {
        type: 'debug',
        message: `ca-recommendation-action-poller: removing ${recommendation.id} from the service array`,
      },
      this.errorTracking,
      this.access.isStaffUser,
    );
    logMessage(
      {
        type: 'debug',
        data: this.recommendationsToFollow,
        message: `ca-recommendation-action-poller: current recommendationsToFollow length: ${
          Object.keys(this.recommendationsToFollow).length
        }`,
      },
      this.errorTracking,
      this.access.isStaffUser,
    );
  }

  resetRecommendationsWithPushAnimation(): void {
    void this.successfulPushLottieAnimationTimerTask.cancelAll();
    this.recommendationsWithPushAnimation.clear();
    this.datesBeingRefreshed.clear();
  }

  /**
   * Will restart pollers for all relevant recommendations in the array that's passed as an argument. Handy when receiving new recommendations after e.g. changing selections.
   * @param {Array} recommendationObjects : array containing recommendation records
   */
  restartPricingAssistantRecommendationActionIdPollers(
    recommendationObjects: Carecommendation[],
  ): void {
    // Filter out the recommendations:
    // In accepting state
    // In pushing state
    const recommendationsToBePolled = recommendationObjects.filter(
      (recommendationObject) => {
        return (
          recommendationObject.isAccepted || recommendationObject.isPushing
        );
      },
    );

    const recommendationsWithBulkPushId: Carecommendation[] = [];
    const recommendationsWithoutBulkPushId: Carecommendation[] = [];

    recommendationsToBePolled.forEach((recommendation) => {
      if (recommendation.bulkPushId) {
        recommendationsWithBulkPushId.push(recommendation);
      } else {
        recommendationsWithoutBulkPushId.push(recommendation);
      }
    });

    if (recommendationsWithBulkPushId.length) {
      const recommendationsByDate: Record<string, Carecommendation[]> = {};

      recommendationsWithBulkPushId.forEach((rec) => {
        const date = rec.fromDate.format('YYYY-MM-DD');

        (recommendationsByDate[date] ??= []).push(rec);
      });

      this.addRecommendationsAffectedByOverridePush(recommendationsByDate);
      this.addBulkPushesToFollow(recommendationsWithBulkPushId);
    }

    if (recommendationsWithoutBulkPushId.length) {
      this.addRecommendationsToFollow(recommendationsWithoutBulkPushId);
    }
  }

  startPolling(options = { isBulkPush: false }): void {
    logMessage(
      {
        type: 'debug',
        message: 'ca-recommendation-action-poller: started polling',
      },
      this.errorTracking,
      this.access.isStaffUser,
    );

    if (options.isBulkPush) {
      void this.pricingAssistantRecommendationActionIdPollerBulkPushTask.perform();
    } else {
      void this.pricingAssistantRecommendationActionIdPollerTask.perform();
    }
  }

  stopPolling(): void {
    logMessage(
      {
        type: 'debug',
        message: 'ca-recommendation-action-poller: stopped polling',
      },
      this.errorTracking,
      this.access.isStaffUser,
    );

    if (this.pricingAssistantRecommendationActionIdPollerTask.isRunning) {
      void this.pricingAssistantRecommendationActionIdPollerTask.cancelAll();
    }

    if (
      this.pricingAssistantRecommendationActionIdPollerBulkPushTask.isRunning
    ) {
      void this.pricingAssistantRecommendationActionIdPollerBulkPushTask.cancelAll();
    }

    this.recommendationsToFollow = {};
    this.bulkPushesToFollow = {};
    this.recommendationAffectedByOverridePush.clear();
    this.datesBeingRefreshed.clear();
  }

  /**
   * Find and unload recommendations that were pushed and not returned in re-requested data.
   * Ember Data does not automatically remove records from the store if they are missing from a subsequent request.
   * @param {Carecommendation[]} actionedRecommendations - Recommendations that were actioned
   * @param {Carecommendation[]} freshRecommendations - Fresh data from API
   */
  private unloadPushedRecommendations(
    actionedRecommendations: Carecommendation[],
    freshRecommendations: Carecommendation[],
  ): void {
    const freshIds = new Set(freshRecommendations.map((rec) => rec.id));
    const recommendationsToUnload = actionedRecommendations.filter(
      (rec) => !freshIds.has(rec.id),
    );

    recommendationsToUnload.forEach((recommendation) => {
      const record = this.store.peekRecord<Carecommendation>(
        'carecommendation',
        recommendation.id as string,
      );

      if (record) {
        this.store.unloadRecord(record);
      }
    });
  }

  /**
   * Unload recommendations removed by an override push
   * NOTE: It can happen that an override causes recommendations to be removed for
   * room types even if they weren't part of the simulation request.
   * @param {Carecommendation[]} freshRecommendations - Fresh recommendations from the API
   * @param {string} fromDate - Date key for the tracked recommendations
   */
  private unloadRecommendationsAffectedByOverridePush(
    freshRecommendations: Carecommendation[],
    fromDate: string,
  ): void {
    const freshIds = new Set(freshRecommendations.map((rec) => rec.id));
    const recommendationsToUnload = this.recommendationAffectedByOverridePush
      .get(fromDate)
      ?.filter((rec) => !freshIds.has(rec.id));

    recommendationsToUnload?.forEach((recommendation) => {
      const recommendationRecord = this.store.peekRecord<Carecommendation>(
        'carecommendation',
        recommendation.id as string,
      );

      if (recommendationRecord) {
        this.store.unloadRecord(recommendationRecord);
      }
    });

    this.removeDateAffectedByOverridePush(fromDate);
  }
}

declare module '@ember/service' {
  interface Registry {
    'pricing-assistant-recommendation-action-poller': PricingAssistantRecommendationActionPollerService;
  }
}
